import { useEffect, useState } from 'react';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterDataFrequency, DemeterPageType, DemeterRegion, DemeterTableDefinitionType } from '../../../Generated/Raven-Demeter/api';
import useCommentaryApi from '../../Apis/Hooks/useCommentaryApiHook';
import AggregateRegionSelector from '../../Components/AggregateRegionSelector/AggregateRegionSelector';
import ProjectionChart from '../../Components/Charts/Projection/ProjectionChart';
import SeasonalChart from '../../Components/Charts/Seasonal/SeasonalChart';
import OriginsAndDestinationsContainer from '../../Components/Containers/OriginsAndDestinations/OriginsAndDestinationsContainer';
import UnitOfMeasureMetricImperialDropdown from '../../Components/Form/Inputs/UnitOfMeasureMetricImperialDropdown';
import TutorialLink from '../../Components/Form/Links/TutorialLink/TutorialLink';
import PageHeaderWithSelections from '../../Components/Headers/PageHeaderWithSelections';
import useTableDefinitionNavigation from '../../Components/Navigation/Hooks/useTableDefinitionNavigationHook';
import SeasonalValueAndPercentTables from '../../Components/Tables/Seasonal/SeasonalValueAndPercentTables';
import useMarketByCommodity from '../../Core/Hooks/useMarketByCommodity';
import useUnitOfMeasure from '../../Core/Hooks/useUnitOfMeasureHook';
import { isRegionCommodityGroupedMarket } from '../../Core/MarketDefinitions';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './ExportsPage.module.scss';

const ExportsPage: React.FC = () => {
    // Constants.
    const [translations] = useLanguage();
    const pageTitle = translations.exports.title;
    const tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyExportsTable;

    // Hooks.
    const [, tableDefinitions, regionCommoditySelection] = useTableDefinitionNavigation(tableDefinitionType);
    const commentary = useCommentaryApi(tableDefinitions?.demeterCommentaryGuid);
    const [unitOfMeasure, setUnitOfMeasure] = useUnitOfMeasure('MetricImperial');
    const marketByCommodity = useMarketByCommodity(tableDefinitionType);
    const [aggregateRegions, setAggregateRegions] = useState<DemeterRegion[]>([]);

    const isYearlyOnly = tableDefinitions?.dataFrequencies?.length === 1 && tableDefinitions.dataFrequencies[0] === DemeterDataFrequency.Yearly;
    const showAllChartsAndTable = isRegionCommodityGroupedMarket(marketByCommodity);

    useEffect(() => {
        setAggregateRegions([]);
    }, [regionCommoditySelection?.region]);

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithSelections
                title={pageTitle}
                tableDefinitionType={tableDefinitionType}
                rightSideOfHeaderComponent={
                    <>
                        <UnitOfMeasureMetricImperialDropdown
                            unitOfMeasure={unitOfMeasure}
                            handleChange={setUnitOfMeasure}
                            testId="ExportsUnitOfMeasureDropdown"
                            track={{ eventCategory: 'Exports', eventAction: 'UnitOfMeasureChanged', eventValue: unitOfMeasure }}
                        />
                        <TutorialLink link={applicationConstants.TutorialLinks.fundamentalsSupplyDemand} />
                    </>
                }
                testId="ExportsPageHeader"
                track={{ eventCategory: 'Exports', eventAction: 'PageHeaderDisplayed' }}
            />
            {showAllChartsAndTable && regionCommoditySelection && (
                <>
                    <div className={styles.exports_main_container}>
                        <div className={styles.exports_projection_chart}>
                            <ProjectionChart
                                title={pageTitle}
                                tableDefinitionType={tableDefinitionType}
                                regionCommoditySelection={regionCommoditySelection!}
                                aggregateRegions={aggregateRegions}
                                unitOfMeasure={unitOfMeasure}
                                pageType={DemeterPageType.Exports}
                                testId="ExportsProjectionChart"
                                track={{
                                    eventCategory: 'Exports',
                                    eventAction: 'ProjectionChart',
                                    eventValue: { tableDefinitionType, regionCommoditySelection },
                                }}
                            />
                        </div>
                        {regionCommoditySelection?.region === DemeterRegion.MajorExporters ? (
                            <div className={styles.exports_commentary_container}>
                                <AggregateRegionSelector
                                    title={translations.region.MajorExporters}
                                    tableDefinitionType={tableDefinitionType}
                                    regionCommoditySelection={regionCommoditySelection!}
                                    handleRegionSelections={setAggregateRegions}
                                    track={{
                                        eventCategory: 'Exports',
                                        eventAction: 'AggregatedRegionSelector',
                                    }}
                                />
                            </div>
                        ) : (
                            tableDefinitions?.demeterCommentaryGuid && (
                                <div className={styles.exports_commentary_container}>
                                    <p>{commentary}</p>
                                </div>
                            )
                        )}
                    </div>
                    <OriginsAndDestinationsContainer
                        tableDefinitionType={tableDefinitionType}
                        regionCommoditySelection={regionCommoditySelection!}
                        unitOfMeasure={unitOfMeasure}
                        testId="ExportsOriginsAndDestinations"
                        track={{
                            eventCategory: 'Exports',
                            eventAction: 'OriginsAndDestinations',
                            eventValue: { tableDefinitionType, regionCommoditySelection, unitOfMeasure },
                        }}
                    />
                </>
            )}
            {!isYearlyOnly && regionCommoditySelection && (
                <div className={styles.exports_main_container}>
                    <div className={styles.exports_seasonal_chart}>
                        <SeasonalChart
                            title={pageTitle}
                            tableDefinitionType={tableDefinitionType}
                            regionCommoditySelection={regionCommoditySelection!}
                            aggregateRegions={aggregateRegions}
                            unitOfMeasure={unitOfMeasure}
                            pageType={DemeterPageType.Exports}
                            testId="ExportsSeasonalChart"
                            track={{
                                eventCategory: 'Exports',
                                eventAction: 'SeasonalChart',
                                eventValue: { tableDefinitionType, regionCommoditySelection },
                            }}
                        />
                    </div>
                </div>
            )}
            {showAllChartsAndTable && !isYearlyOnly && (
                <SeasonalValueAndPercentTables
                    title={pageTitle}
                    tableDefinitionType={tableDefinitionType}
                    regionCommoditySelection={regionCommoditySelection!}
                    aggregateRegions={aggregateRegions}
                    unitOfMeasure={unitOfMeasure}
                    testId="ExportsSeasonalValueAndPercentTables"
                    track={{
                        eventCategory: 'Exports',
                        eventAction: 'SeasonalValueAndPercentTables',
                        eventValue: { tableDefinitionType, regionCommoditySelection },
                    }}
                />
            )}
        </div>
    );
};
export default ExportsPage;
